import type React from 'react';
import {useMemo} from 'react';
import {useGetGeneralCarousel} from '../../../../core/repositories/generalCarouselRepository';
import {useMediaQuery} from '../../../../shared/services/useMediaQuery';
import {GeneralCarouselPresentation} from './presentation';

type Props = {
  componentId: string;
  /** displayOrderの開始範囲（0-based）。startIndex=0なら displayOrder=1から */
  startIndex?: number;
  /** displayOrderの終了範囲（0-based）。endIndex=5なら displayOrder=5まで */
  endIndex?: number;
  autoScroll?: boolean;
  itemScrollInterval?: number;
  infiniteLoop?: boolean;
};

export const GeneralCarouselContainer: React.FC<Props> = ({
  componentId,
  startIndex = 0,
  endIndex,
  autoScroll = false,
  itemScrollInterval = 2000,
  infiniteLoop = false,
}) => {
  const {data, isLoading, error} = useGetGeneralCarousel();
  const {isPC} = useMediaQuery();

  // データ処理をuseMemoで実行（hooks呼び出し順序を一定に保つ）
  const sortedAndFilteredItems = useMemo(() => {
    // エラーや空データの場合は空配列を返す
    if (error || (data && !data.ok) || !data?.value?.items) {
      return [];
    }

    const items = data.value.items;

    // displayOrderでソートし、指定された範囲のアイテムをフィルタリング
    const sortedItems = items.sort((a, b) => a.displayOrder - b.displayOrder);

    // displayOrderの値に基づいてフィルタリング（startIndex+1 ≤ displayOrder ≤ endIndex）
    const minDisplayOrder = startIndex + 1;
    const maxDisplayOrder = endIndex ?? 10;

    return sortedItems.filter(
      (item) =>
        item.displayOrder >= minDisplayOrder &&
        item.displayOrder <= maxDisplayOrder,
    );
  }, [data, error, startIndex, endIndex]);

  // アイテムをランダムに並べ替え（Fisher-Yatesアルゴリズム）
  // useMemoでメモ化し、アイテムリストが変わるまで同じ順序を維持
  const randomizedItems = useMemo(() => {
    const shuffled = [...sortedAndFilteredItems];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const itemI = shuffled[i];
      const itemJ = shuffled[j];
      if (itemI && itemJ) {
        shuffled[i] = itemJ;
        shuffled[j] = itemI;
      }
    }
    return shuffled;
  }, [sortedAndFilteredItems]);

  // エラーや空データの場合は何も表示しない
  if (error || (data && !data.ok) || !data?.value?.items) {
    return null;
  }

  return (
    <GeneralCarouselPresentation
      componentId={componentId}
      items={randomizedItems}
      isLoading={isLoading}
      autoScroll={autoScroll}
      itemScrollInterval={itemScrollInterval}
      infiniteLoop={infiniteLoop}
      isPC={isPC}
    />
  );
};
